import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient, QueryKey } from '@tanstack/react-query';
import { subscriptionManager } from '../lib/subscription-manager';
import { logger } from '../utils/logger';
import { topicKeys } from './queries/topicKeys';
import { TablePayloadMap, GetPayloadType, RealtimeEventType } from '../types/realtime';

/**
 * Shared hook for debounced query invalidation to prevent memory leaks and code duplication
 */
function useDebouncedInvalidation(enableDebouncing: boolean = true, debounceDelay: number = 300) {
  const queryClient = useQueryClient();
  const debounceTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const mountedRef = useRef(true);

  const debouncedInvalidate = useCallback(
    (queryKey: QueryKey, context?: any) => {
      if (!enableDebouncing) {
        queryClient.invalidateQueries({
          queryKey,
          refetchType: 'active',
        });
        return;
      }

      const keyString = JSON.stringify(queryKey);

      // Clear existing timeout for this query key
      const existingTimeout = debounceTimeouts.current.get(keyString);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // Set new timeout
      const timeout = setTimeout(() => {
        if (mountedRef.current) {
          queryClient.invalidateQueries({
            queryKey,
            refetchType: 'active',
          });

          logger.realtime('Debounced query invalidation executed', {
            queryKey,
            context,
          });
        }
        debounceTimeouts.current.delete(keyString);
      }, debounceDelay);

      debounceTimeouts.current.set(keyString, timeout);
    },
    [enableDebouncing, debounceDelay]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      debounceTimeouts.current.forEach(timeout => clearTimeout(timeout));
      debounceTimeouts.current.clear();
    };
  }, []);

  return debouncedInvalidate;
}

/**
 * Configuration for a single subscription with improved type safety
 */
export interface SubscriptionConfig<T extends keyof TablePayloadMap = keyof TablePayloadMap> {
  /** Database table to subscribe to */
  table: T;
  /** Query key to invalidate when changes occur */
  queryKey: QueryKey;
  /** Optional event type filter (defaults to '*' for all events) */
  event?: RealtimeEventType | '*';
  /** Optional schema (defaults to 'public') */
  schema?: string;
  /** Additional filter conditions */
  filter?: string;
  /** Optional custom handler with proper typing */
  customHandler?: (payload: GetPayloadType<T>) => void;
}

/**
 * Custom hook for managing topic-level realtime subscriptions
 *
 * This hook eliminates code duplication by providing a centralized way to set up
 * multiple subscriptions for a topic with proper cleanup and error handling.
 *
 * @param topicId - The topic ID to subscribe to
 * @param subscriptions - Array of subscription configurations
 * @param options - Additional options for subscription behavior
 */
export function useTopicSubscription(
  topicId: string | null,
  subscriptions: SubscriptionConfig[],
  options: {
    /** Whether to enable debounced invalidation (default: true) */
    enableDebouncing?: boolean;
    /** Debounce delay in milliseconds (default: 300) */
    debounceDelay?: number;
  } = {}
) {
  const { enableDebouncing = true, debounceDelay = 300 } = options;

  // Use shared debounced invalidation logic
  const debouncedInvalidate = useDebouncedInvalidation(enableDebouncing, debounceDelay);

  // Use ref to always get the latest debouncedInvalidate function
  const debouncedInvalidateRef = useRef(debouncedInvalidate);
  debouncedInvalidateRef.current = debouncedInvalidate;

  // Track component mount state to prevent race conditions
  const mountedRef = useRef(true);
  const cleanupRef = useRef<(() => void) | undefined>();

  useEffect(() => {
    // Reset mount state when effect runs
    mountedRef.current = true;

    if (!topicId || subscriptions.length === 0) {
      return;
    }

    logger.realtime('Setting up topic subscriptions', {
      topicId,
      subscriptionCount: subscriptions.length,
      tables: subscriptions.map(s => s.table),
      // Add stack trace to identify which component is calling this
      caller: new Error().stack?.split('\n')[2]?.trim() || 'unknown',
    });

    // Add a small delay to prevent rapid subscription attempts during fast re-renders
    const setupTimer = setTimeout(async () => {
      if (!mountedRef.current) {
        return; // Component unmounted during delay
      }

      const setupSubscriptions = async () => {
        try {
          const unsubscribeFunctions = await Promise.allSettled(
            subscriptions.map(async config => {
              try {
                // Use a more stable channel name that doesn't include the event
                // This prevents channel recreation when event changes
                const channelName = `topic-${topicId}-${config.table}`;
                const filter = config.filter || `topic_id=eq.${topicId}`;

                // Create a handler that works with the subscription manager
                const handler = (payload: any) => {
                  try {
                    // Extract common information from the payload
                    const entityId = payload.new?.id || payload.old?.id || 'unknown';

                    logger.realtime('Real-time change detected', {
                      table: config.table,
                      event: payload.eventType,
                      entityId,
                      topicId,
                      timestamp: payload.commit_timestamp,
                    });

                    // Call custom handler if provided
                    if (config.customHandler) {
                      try {
                        // For custom handlers, we'll pass the raw payload for now
                        config.customHandler(payload as any);
                      } catch (handlerError) {
                        logger.error('Error in custom subscription handler', {
                          table: config.table,
                          topicId,
                          error:
                            handlerError instanceof Error
                              ? handlerError.message
                              : String(handlerError),
                        });
                      }
                    }

                    // Use debounced invalidation via ref to get latest function
                    debouncedInvalidateRef.current(config.queryKey, {
                      table: config.table,
                      event: payload.eventType,
                      entityId,
                      topicId,
                    });
                  } catch (processingError) {
                    logger.error('Error processing realtime payload', {
                      table: config.table,
                      topicId,
                      error:
                        processingError instanceof Error
                          ? processingError.message
                          : String(processingError),
                    });
                  }
                };

                const unsubscribeFn = await subscriptionManager.subscribe(
                  {
                    channelName,
                    table: config.table,
                    event: config.event || '*',
                    schema: config.schema || 'public',
                    filter,
                  },
                  handler
                );

                logger.realtime('Successfully set up subscription', {
                  table: config.table,
                  topicId,
                  channelName,
                });

                return unsubscribeFn;
              } catch (subscriptionError) {
                logger.error('Failed to set up individual subscription', {
                  table: config.table,
                  topicId,
                  error:
                    subscriptionError instanceof Error
                      ? subscriptionError.message
                      : String(subscriptionError),
                });

                // Return a no-op unsubscribe function for failed subscriptions
                return () => {};
              }
            })
          );

          // Filter out failed subscriptions but continue with successful ones
          const successfulUnsubscribes = unsubscribeFunctions
            .filter(
              (result): result is PromiseFulfilledResult<() => void> =>
                result.status === 'fulfilled'
            )
            .map(result => result.value);

          // Log any failures
          const failedCount = unsubscribeFunctions.length - successfulUnsubscribes.length;
          if (failedCount > 0) {
            logger.warn('Some subscriptions failed to set up', {
              topicId,
              totalSubscriptions: subscriptions.length,
              failedCount,
              successfulCount: successfulUnsubscribes.length,
            });

            // Update health status to reflect partial failures
            if (mountedRef.current) {
              setSubscriptionHealth(prev => ({
                ...prev,
                isHealthy: successfulUnsubscribes.length > 0,
                lastError:
                  failedCount > 0
                    ? `${failedCount} of ${subscriptions.length} subscriptions failed`
                    : undefined,
              }));
            }
          }

          // Return combined cleanup function
          return () => {
            successfulUnsubscribes.forEach(unsubscribeFn => unsubscribeFn());

            logger.realtime('Topic subscriptions cleaned up', {
              topicId,
              subscriptionCount: successfulUnsubscribes.length,
            });
          };
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);

          logger.error('Failed to set up topic subscriptions', {
            topicId,
            subscriptions: subscriptions.map(s => s.table),
            error: errorMessage,
          });

          // Update health status to reflect error
          if (mountedRef.current) {
            setSubscriptionHealth(prev => ({
              ...prev,
              isHealthy: false,
              lastError: errorMessage,
            }));
          }

          // Return no-op cleanup function
          return () => {};
        }
      };

      setupSubscriptions().then(cleanupFn => {
        if (mountedRef.current) {
          cleanupRef.current = cleanupFn;
        } else {
          // Component already unmounted, cleanup immediately
          cleanupFn();
        }
      });
    }, 100); // 100ms delay to debounce rapid re-renders

    return () => {
      mountedRef.current = false;
      clearTimeout(setupTimer);
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [topicId, JSON.stringify(subscriptions), enableDebouncing, debounceDelay]);

  // Track subscription health for error boundary integration
  const [subscriptionHealth, setSubscriptionHealth] = useState<{
    isHealthy: boolean;
    lastError?: string;
    lastSuccessfulConnection?: Date;
  }>({
    isHealthy: true,
  });

  // Update health status when subscriptions succeed or fail
  useEffect(() => {
    if (topicId && subscriptions.length > 0) {
      setSubscriptionHealth(prev => ({
        ...prev,
        isHealthy: true,
        lastSuccessfulConnection: new Date(),
      }));
    }
  }, [topicId, subscriptions.length]);

  // Return subscription status for debugging and error boundaries
  return {
    isActive: !!topicId && subscriptions.length > 0,
    subscriptionCount: subscriptions.length,
    topicId,
    health: subscriptionHealth,
    // Method to manually trigger error state (for testing)
    _triggerError: (error: string) => {
      setSubscriptionHealth(prev => ({
        ...prev,
        isHealthy: false,
        lastError: error,
      }));
    },
  };
}

/**
 * Custom hook for managing project-level topic list subscriptions
 *
 * This hook is specifically designed for subscribing to changes in the topics table
 * for a given project, used by components like TopicList.
 *
 * @param projectId - The project ID to subscribe to topics for
 * @param options - Additional options for subscription behavior
 */
export function useProjectTopicSubscription(
  projectId: string | null,
  options: {
    /** Whether to enable debounced invalidation (default: true) */
    enableDebouncing?: boolean;
    /** Debounce delay in milliseconds (default: 300) */
    debounceDelay?: number;
  } = {}
) {
  const { enableDebouncing = true, debounceDelay = 300 } = options;

  // Use shared debounced invalidation logic
  const debouncedInvalidate = useDebouncedInvalidation(enableDebouncing, debounceDelay);

  // Use ref to always get the latest debouncedInvalidate function
  const debouncedInvalidateRef = useRef(debouncedInvalidate);
  debouncedInvalidateRef.current = debouncedInvalidate;

  // Track component mount state to prevent race conditions
  const mountedRef = useRef(true);
  const cleanupRef = useRef<(() => void) | undefined>();

  useEffect(() => {
    // Reset mount state when effect runs
    mountedRef.current = true;

    if (!projectId) {
      return;
    }

    logger.realtime('Setting up project topic subscriptions', {
      projectId,
      table: 'topics',
    });

    const setupSubscriptions = async () => {
      try {
        const channelName = `project-topics-${projectId}`;
        const filter = `project_id=eq.${projectId}`;

        // Create a handler for topic changes
        const handler = (payload: any) => {
          const entityId = payload.new?.id || payload.old?.id || 'unknown';

          logger.realtime('Real-time topic change detected', {
            table: 'topics',
            event: payload.eventType,
            entityId,
            projectId,
            timestamp: payload.commit_timestamp,
          });

          // Use debounced invalidation for the project's topic list via ref
          debouncedInvalidateRef.current(topicKeys.byProject(projectId), {
            table: 'topics',
            event: payload.eventType,
            entityId,
            projectId,
          });
        };

        const unsubscribeFn = await subscriptionManager.subscribe(
          {
            channelName,
            table: 'topics',
            event: '*',
            schema: 'public',
            filter,
          },
          handler
        );

        return () => {
          unsubscribeFn();

          logger.realtime('Project topic subscriptions cleaned up', {
            projectId,
          });
        };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);

        logger.error('Failed to set up project topic subscriptions', {
          projectId,
          error: errorMessage,
        });

        // Update health status to reflect error
        if (mountedRef.current) {
          setSubscriptionHealth(prev => ({
            ...prev,
            isHealthy: false,
            lastError: errorMessage,
          }));
        }

        // Return no-op cleanup function
        return () => {};
      }
    };

    setupSubscriptions().then(cleanupFn => {
      if (mountedRef.current) {
        cleanupRef.current = cleanupFn;
      } else {
        // Component already unmounted, cleanup immediately
        cleanupFn();
      }
    });

    return () => {
      mountedRef.current = false;
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [projectId, enableDebouncing, debounceDelay]);

  // Track subscription health for error boundary integration
  const [subscriptionHealth, setSubscriptionHealth] = useState<{
    isHealthy: boolean;
    lastError?: string;
    lastSuccessfulConnection?: Date;
  }>({
    isHealthy: true,
  });

  // Update health status when subscriptions succeed or fail
  useEffect(() => {
    if (projectId) {
      setSubscriptionHealth(prev => ({
        ...prev,
        isHealthy: true,
        lastSuccessfulConnection: new Date(),
      }));
    }
  }, [projectId]);

  // Return subscription status for debugging and error boundaries
  return {
    isActive: !!projectId,
    projectId,
    health: subscriptionHealth,
    // Method to manually trigger error state (for testing)
    _triggerError: (error: string) => {
      setSubscriptionHealth(prev => ({
        ...prev,
        isHealthy: false,
        lastError: error,
      }));
    },
  };
}

/**
 * Helper function to create channel names consistently
 */
export function createChannelName(table: string, topicId: string): string {
  return `topic-${table}-${topicId}`;
}
